import sha256 from 'js-sha256';


class EcommerceDataMapper {
  constructor(store) {
    this.store = store;
  }

  static init(store) {
    return new EcommerceDataMapper(store);
  }

  mapItemData({ itemInfo }) {
    const storeData = this._mapMerchantStoreData();
    const itemsData = this._mapItemDetailsData(itemInfo);
    const userData = this._mapUserData();

    const eventData = {
      ...storeData,
      ...itemsData,
      ...userData,
      currency: 'USD'
    };

    return eventData;
  }

  mapCheckoutData({ itemInfo, userInfo, orderInfo, paymentInfo }) {
    const storeData = this._mapMerchantStoreData();
    const itemsData = this._mapItemDetailsData(itemInfo);
    const paymentData = this._mapPaymentData(paymentInfo);
    const orderData = this._mapOrderData(orderInfo);
    const userData = this._mapUserData(userInfo);

    const eventData = {
      ...storeData,
      ...itemsData,
      ...paymentData,
      ...orderData,
      ...userData,
      currency: 'USD'
    };

    return eventData;
  }

  // eslint-disable-next-line class-methods-use-this
  mapPromotionData({ offer }) {
    const eventData = {
      promotion_id: offer.cardfreeOfferPublicId,
      promotion_name: offer.name || offer.promoCode || offer.title
    };

    return eventData;
  }

  _mapOrderData(orderInfo = {}) {
    const { externalOrderId, ticketNumber } = orderInfo;

    const { orderId } = this.store.getters['orders/order'];

    const { pickupType, fulfillmentType } = this.store.getters['session/orderDetails'];

    return {
      transaction_id: externalOrderId || ticketNumber || orderId,
      order_mode: fulfillmentType === 'pick-up' ? pickupType : null,
      menu_type: fulfillmentType
    };
  }

  _mapUserData(userInfo = null) {
    const {
      firstName,
      lastName,
      email,
      primarySmsNumber,
      isSignedIn
    } = this.store.getters['session/currentUser'];

    const hashProperty = prop => (prop ? sha256(prop) : null);

    if (isSignedIn) {
      return {
        first_name: hashProperty(firstName),
        last_name: hashProperty(lastName),
        phone_number: hashProperty(primarySmsNumber),
        email_address: hashProperty(email)
      };
    }

    if (userInfo) {
      return {
        full_name: hashProperty(userInfo.fullName),
        phone_number: hashProperty(userInfo.phoneNumber),
        email_address: hashProperty(userInfo.email)
      };
    }

    /**
     * If user is not signed in
     * or has not yet been prompted for contact information at checkout then
     * return empty object
    */
    return {};
  }

  _mapItemDetailsData(menuItems) {
    const { gtmLocationId, businessLocationId, storeId } = this.store.getters['locations/selectedLocation'];

    const items = menuItems?.map((item) => {
      const itemDetails = {
        item_id: item.menuItemId,
        item_name: item.name || item.displayName || item.itemDescription,
        item_category: item.categoryId,
        price: item.price || item.priceTotal,
        isUpsell: item.isUpsell ?? false,
        location_id: gtmLocationId || businessLocationId || storeId
      };

      if (item.modifiers) {
        itemDetails.item_modifier = item.modifiers;
      }

      if (item.quantity) {
        itemDetails.quantity = item.quantity;
      }

      return itemDetails;
    });

    return { items };
  }

  // eslint-disable-next-line class-methods-use-this
  _mapPaymentData(paymentInfo) {
    const paymentData = {};

    if (paymentInfo?.total) {
      paymentData.value = paymentInfo.total;
    }

    if (paymentInfo?.tax) {
      paymentData.tax = paymentInfo.tax;
    }

    if (paymentInfo?.tip) {
      paymentData.tip_amount = paymentInfo.tip;
    }

    if (paymentInfo?.promoCode) {
      paymentData.coupon = paymentInfo.promoCode;
    }

    if (paymentInfo?.discount) {
      paymentData.discount_amount = paymentInfo.discount;
    }

    if (paymentInfo?.paymentType) {
      paymentData.payment_type = paymentInfo.paymentType;
    }

    return paymentData;
  }

  _mapMerchantStoreData() {
    const merchantName = this.store.getters['merchant/merchant'].name;

    const { gtmLocationId, businessLocationId, storeId, description: storeName } = this.store.getters['locations/selectedLocation'];

    const storeData = {
      item_brand: merchantName,
      store_id: gtmLocationId || businessLocationId || storeId,
      store: storeName
    };

    return storeData;
  }
}

export default EcommerceDataMapper;
